// frontend/src/features/citysense/housing-stock-modeller/hooks/useHousingState.ts

import { useState, useEffect, useMemo } from "react";
import { HousingPolygon, ViewMode } from "../types";
import { loadHousingPolygons } from "../utils/geojsonLoader";
import { computeSummary } from "../utils/calculator";
import { runHousingOptimizer } from "../utils/optimizer";

export function useHousingState() {
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState<ViewMode>("scenario");

  const [baselinePolygons, setBaselinePolygons] = useState<HousingPolygon[]>(
    [],
  );
  const [workingPolygons, setWorkingPolygons] = useState<HousingPolygon[]>([]);
  const [archetypeColors, setArchetypeColors] = useState<Map<string, string>>(
    new Map(),
  );

  const [selectedPolygonId, setSelectedPolygonId] = useState<string | null>(
    null,
  );
  const [upgradedByOptimizer, setUpgradedByOptimizer] = useState<Set<string>>(
    new Set(),
  );

  // Load GeoJSON on mount
  useEffect(() => {
    fetch("/data/housing_stock_modeller/households.geojson")
      .then((res) => res.json())
      .then((geojson) => {
        const { polygons, archetypeColors: colors } =
          loadHousingPolygons(geojson);
        setBaselinePolygons(polygons);
        setWorkingPolygons(polygons);
        setArchetypeColors(colors);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load housing GeoJSON:", err);
        setIsLoading(false);
      });
  }, []);

  const summary = useMemo(
    () => computeSummary(workingPolygons),
    [workingPolygons],
  );

  const handleArchetypeChange = (polygonId: string, newArchetype: string) => {
    setWorkingPolygons((prev) =>
      prev.map((p) =>
        p.id === polygonId ? { ...p, archetype: newArchetype } : p,
      ),
    );
    setUpgradedByOptimizer(new Set()); // Clear optimizer highlights
  };

  const handleReset = () => {
    setWorkingPolygons(baselinePolygons);
    setSelectedPolygonId(null);
    setUpgradedByOptimizer(new Set());
  };

  const handleRunOptimizer = (
    baseArchetype: string,
    targetArchetype: string,
    minDeprivationIndex: string,
  ) => {
    const result = runHousingOptimizer(
      baselinePolygons,
      baseArchetype,
      targetArchetype,
      minDeprivationIndex,
    );

    setWorkingPolygons(
      baselinePolygons.map((p) =>
        result.upgradedPolygons.has(p.id)
          ? { ...p, archetype: targetArchetype }
          : p,
      ),
    );
    setUpgradedByOptimizer(result.upgradedPolygons);
    setViewMode("optimizer");

    return result;
  };

  return {
    isLoading,
    viewMode,
    setViewMode,
    baselinePolygons,
    workingPolygons,
    archetypeColors,
    selectedPolygonId,
    setSelectedPolygonId,
    upgradedByOptimizer,
    summary,
    handleArchetypeChange,
    handleReset,
    handleRunOptimizer,
  };
}
